import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getApiUrl } from '../api';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../context/LanguageContext';
import styles from './OAuthCallbackPage.module.css';
import Navbar from '../components/Navbar';

// ── Inline SVG ──────────────────────────────────────────────────────────────
const Icon = ({ d, size = 20, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    {Array.isArray(d)
      ? d.map((path, i) => <path key={i} d={path} />)
      : <path d={d} />}
  </svg>
);

const GoogleLogo = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const Spinner = ({ size = 40 }) => (
  <svg
    className={styles.spinner}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

// ── State: loading / success / error / denied ───────────────────────────────
const OAuthCallbackPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const code = searchParams.get('code');
  const errorParam = searchParams.get('error'); // 'access_denied' when user clicks Deny

  const [phase, setPhase] = useState('loading'); // 'loading' | 'success' | 'error' | 'denied'
  const [errorMsg, setErrorMsg] = useState('');
  const [accountInfo, setAccountInfo] = useState(null); // { email, name, locationName }

  const token = localStorage.getItem('token1');
  const hasRun = useRef(false);

  useEffect(() => {
    // ── User clicked "Deny" on the Google consent screen ────────────────
    if (errorParam === 'access_denied') {
      setPhase('denied');
      return;
    }

    if (!code) {
      setErrorMsg('No authorization code was found in the URL. Please try connecting again.');
      setPhase('error');
      return;
    }

    if (hasRun.current) return;
    hasRun.current = true;

    const exchangeCode = async () => {
      try {
        const res = await fetch(getApiUrl('/api/auth/google/callback'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ code }),
        });

        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch {
          throw new Error(
            `Server returned a non-JSON response (${res.status}). ` +
            'Make sure the backend /api/auth/google/callback route is set up.'
          );
        }

        if (!res.ok) {
          throw new Error(data?.message || `Token exchange failed (${res.status})`);
        }

        // Optionally store any returned info in localStorage for quick UI display
        if (data.location) {
          const existing = JSON.parse(localStorage.getItem('googleLocations') || '[]');
          const updated = [...existing.filter(l => l.locationId !== data.location.locationId), data.location];
          localStorage.setItem('googleLocations', JSON.stringify(updated));
        }

        setAccountInfo({
          email: data.email || data.location?.googleAccountId || null,
          name: data.name || null,
          locationName: data.location?.locationId || null,
        });
        setPhase('success');
      } catch (err) {
        setErrorMsg(err.message || 'An unexpected error occurred.');
        setPhase('error');
      }
    };

    exchangeCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, errorParam]);

  // ── Auto-navigate to dashboard after success ────────────────────────────
  useEffect(() => {
    if (phase !== 'success') return;
    const timer = setTimeout(() => navigate('/dashboard'), 4000);
    return () => clearTimeout(timer);
  }, [phase, navigate]);

  return (
    <div className={styles.page}>
      <Navbar className={styles.navbar} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', maxWidth: '1200px', margin: '0 auto', padding: '16px 24px' }} brandClassName={styles.navBrand} brand={<><span className={styles.brandDot} />ReplyShoot</>}>
        <LanguageToggle />
      </Navbar>

      <main className={styles.main}>
        <div className={styles.card}>
          {/* ── Loading ──────────────────────────────────────────── */}
          {phase === 'loading' && (
            <>
              <div className={styles.iconWrap}>
                <Spinner size={48} />
              </div>
              <h1 className={styles.title}>{t('connectingAccount', 'Connecting your account…')}</h1>
              <p className={styles.sub}>
                {t('exchangingCode', "We're exchanging your authorization code with Google. This should only take a moment.")}
              </p>
            </>
          )}

          {/* ── Success ──────────────────────────────────────────── */}
          {phase === 'success' && (
            <>
              <div className={`${styles.iconWrap} ${styles.iconSuccess}`}>
                <Icon d="M9 11l3 3L22 4" size={36} />
              </div>
              <h1 className={styles.title}>{t('connectionSuccessful', 'Google Account Connected!')}</h1>
              <p className={styles.sub}>
                {t('googleConnectedSub', 'Your Google Business Profile is now linked to ReplyShoot. We can now fetch and reply to your reviews automatically.')}
              </p>

              {accountInfo && (
                <div className={styles.accountCard}>
                  <div className={styles.accountCardHeader}>
                    <GoogleLogo size={20} />
                    <span>{t('connectedAccount', 'Connected Account')}</span>
                  </div>
                  {accountInfo.name && (
                    <div className={styles.accountRow}>
                      <span className={styles.accountKey}>Name</span>
                      <span className={styles.accountVal}>{accountInfo.name}</span>
                    </div>
                  )}
                  {accountInfo.email && (
                    <div className={styles.accountRow}>
                      <span className={styles.accountKey}>Account</span>
                      <span className={styles.accountVal}>{accountInfo.email}</span>
                    </div>
                  )}
                  {accountInfo.locationName && (
                    <div className={styles.accountRow}>
                      <span className={styles.accountKey}>Location ID</span>
                      <span className={`${styles.accountVal} ${styles.mono}`}>{accountInfo.locationName}</span>
                    </div>
                  )}
                </div>
              )}

              <p className={styles.redirectNote}>
                <Icon d="M5 3l14 9-14 9V3z" size={13} />
                {t('autoRedirecting', 'Redirecting you to the dashboard in a few seconds...')}
              </p>

              <div className={styles.actions}>
                <Link to="/dashboard" className={styles.primaryBtn}>
                  <Icon d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" size={16} />
                  {t('goToDashboard', 'Go to Dashboard')}
                </Link>
                <Link to="/connect-google" className={styles.ghostBtn}>
                  {t('connectAnotherAccount', 'Connect Another Account')}
                </Link>
              </div>
            </>
          )}

          {/* ── Denied ───────────────────────────────────────────── */}
          {phase === 'denied' && (
            <>
              <div className={`${styles.iconWrap} ${styles.iconWarning}`}>
                <Icon d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" size={36} />
              </div>
              <h1 className={styles.title}>{t('connectionDenied', 'Permission Denied')}</h1>
              <p className={styles.sub}>
                {t('connectionDeniedSub', "You cancelled or denied access on the Google authorization screen. No data was collected. You can try again whenever you're ready.")}
              </p>
              <div className={styles.actions}>
                <Link to="/connect-google" className={styles.primaryBtn}>
                  <Icon d="M1 4v6h6M23 20v-6h-6" size={16} />
                  {t('tryConnectingAgain', 'Try Again')}
                </Link>
                <Link to="/" className={styles.ghostBtn}>
                  {t('backToHome', 'Back to Home')}
                </Link>
              </div>
            </>
          )}

          {/* ── Error ────────────────────────────────────────────── */}
          {phase === 'error' && (
            <>
              <div className={`${styles.iconWrap} ${styles.iconError}`}>
                <Icon d="M18 6 6 18M6 6l12 12" size={36} />
              </div>
              <h1 className={styles.title}>{t('connectionFailed', 'Connection Failed')}</h1>
              <p className={styles.sub}>
                {t('connectionFailedSub', 'Something went wrong while connecting your Google account. Please try again.')}
              </p>
              {errorMsg && (
                <div className={styles.errorBox} role="alert">
                  <Icon
                    d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                    size={15}
                  />
                  <span>{errorMsg}</span>
                </div>
              )}
              <div className={styles.actions}>
                <Link to="/connect-google" className={styles.primaryBtn}>
                  <Icon d="M1 4v6h6M23 20v-6h-6" size={16} />
                  {t('tryConnectingAgain', 'Try Again')}
                </Link>
                <Link to="/dashboard" className={styles.ghostBtn}>
                  {t('goToDashboard', 'Dashboard')}
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default OAuthCallbackPage;
